import { useState, useDeferredValue } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Search,
  AlertTriangle,
  Trash2,
  Loader2,
  AlertCircle,
  Settings,
  Cpu,
} from "lucide-react"
import { RetireDeviceComposer, FaultDeviceComposer } from "@/forms/sensory"
import { useToast } from "@/contexts/ToastContext"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import type { GetDevicesRegistry } from "@generated-schemas"
import type { InferResponse } from "@/types/registryTypes"

type Device = InferResponse<typeof GetDevicesRegistry>["content"]["devices"][number]
const statusConfig: Record<
  string,
  {
    variant: "default" | "secondary" | "destructive" | "outline" | "warning" | "info" | "success"
    label: string
    className?: string
  }
> = {
  procured: { variant: "warning", label: "Procured" },
  provisioned: { variant: "success", label: "Active" },
  retired: { variant: "secondary", label: "Retired" },
  faulty: { variant: "destructive", label: "Faulty" },
  returned: { variant: "warning", label: "Returned" },
  ordered: { variant: "info", label: "Ordered" },
  shipped: { variant: "info", label: "Shipped" },
  received: { variant: "info", label: "Received" },
  testing: { variant: "warning", label: "Testing" },
  tested: { variant: "success", label: "Tested" },
  available: { variant: "success", label: "Available" },
  live: { variant: "success", label: "Live" },
}

export function DeviceManagement() {
  const { toast } = useToast()
  const [searchQuery, setSearchQuery] = useState("")
  const deferredSearchQuery = useDeferredValue(searchQuery)
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null)
  const [isRetireDialogOpen, setIsRetireDialogOpen] = useState(false)
  const [isFaultDialogOpen, setIsFaultDialogOpen] = useState(false)

  // Fetch devices from API
  const {
    data: devicesResponse,
    isLoading,
    error,
    refetch,
  } = useApiQuery("getDevices")

  const devices = devicesResponse?.content?.devices || []

  // Filter devices - show only live devices
  const eligibleDevices = devices.filter((device) => {
    const status = String(device.deviceStatus)
    // Show only devices that are live
    return status === "live"
  })

  const filteredDevices = eligibleDevices.filter((device) =>
    device.deviceNumber.toString().includes(deferredSearchQuery) ||
    device.deviceTypeName?.toLowerCase().includes(deferredSearchQuery.toLowerCase())
  )

  const handleRetire = (device: Device) => {
    setSelectedDevice(device)
    setIsRetireDialogOpen(true)
  }

  const handleMarkFaulty = (device: Device) => {
    setSelectedDevice(device)
    setIsFaultDialogOpen(true)
  }

  return (
    <div className="space-y-4">
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Settings className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">Device Management</CardTitle>
                <CardDescription>Retire devices or mark them as faulty</CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by device number or type..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{filteredDevices.length}</span>
                <span>eligible device{filteredDevices.length !== 1 ? 's' : ''} found</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <div className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                <span className="text-muted-foreground">Loading devices...</span>
              </div>
            </div>
          ) : error ? (
            <div className="flex h-64 flex-col items-center justify-center gap-4">
              <AlertCircle className="h-8 w-8 text-destructive" />
              <p className="text-lg font-medium">Failed to load devices</p>
              <p className="text-sm text-muted-foreground">{error.message}</p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          ) : filteredDevices.length > 0 ? (
            <div className="divide-y">
              {filteredDevices.map((device) => {
                const status = statusConfig[device.deviceStatus] || statusConfig.provisioned
                return (
                  <div
                    key={(device as any).id ?? device.deviceNumber}
                    className="p-4 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                          <Cpu className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-lg">
                              {device.deviceNumber || `Device #${device.deviceNumber}`}
                            </h3>
                            <Badge
                              variant={status.variant}
                              className={`${status.className} font-medium`}
                            >
                              {status.label}
                            </Badge>
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                            <span>Device #{device.deviceNumber}</span>
                            {device.deviceTypeName && (
                              <span>Type: {device.deviceTypeName}</span>
                            )}
                            {device.deviceTypeCategory && (
                              <span>Category: {device.deviceTypeCategory}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleMarkFaulty(device)}
                        >
                          <AlertTriangle className="h-4 w-4 mr-2" />
                          Mark as Faulty
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={() => handleRetire(device)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Retire Device
                        </Button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="flex h-64 flex-col items-center justify-center gap-4 py-8">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                <Search className="h-8 w-8 text-muted-foreground/50" />
              </div>
              <div className="text-center space-y-2">
                <p className="text-lg font-semibold">
                  {searchQuery ? "No devices found" : "No eligible devices"}
                </p>
                <p className="text-sm text-muted-foreground">
                  {searchQuery
                    ? `No devices match "${searchQuery}"`
                    : "Only devices that are provisioned can be retired or marked as faulty"}
                </p>
              </div>
              {searchQuery && (
                <Button variant="outline" size="sm" onClick={() => setSearchQuery("")} className="gap-2">
                  <Search className="h-4 w-4" />
                  Clear search
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Retire Device Dialog */}
      <Dialog open={isRetireDialogOpen} onOpenChange={setIsRetireDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Retire Device</DialogTitle>
            <DialogDescription>
              Record retirement details for this device.
            </DialogDescription>
          </DialogHeader>
          {selectedDevice && (
            <div className="space-y-4 py-4">
              <div className="rounded-md bg-muted p-3 text-sm">
                <p><strong>Device:</strong> {selectedDevice.deviceNumber || `Device #${selectedDevice.deviceNumber}`}</p>
                <p><strong>Device Number:</strong> #{selectedDevice.deviceNumber}</p>
                {selectedDevice.deviceTypeName && (
                  <p><strong>Type:</strong> {selectedDevice.deviceTypeName}</p>
                )}
              </div>
              <RetireDeviceComposer
                deviceNumber={selectedDevice.deviceNumber}
                showHeader={false}
                onSuccess={() => {
                  setIsRetireDialogOpen(false)
                  setSelectedDevice(null)
                  refetch()
                }}
                onError={(error) => {
                  log.error("Failed to retire device:", error)
                  toast({ title: 'Error', description: `Failed to retire device: ${error.message}`, type: 'error' })
                }}
                onCancel={() => {
                  setIsRetireDialogOpen(false)
                  setSelectedDevice(null)
                }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mark as Faulty Dialog */}
      <Dialog open={isFaultDialogOpen} onOpenChange={setIsFaultDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Mark Device as Faulty</DialogTitle>
            <DialogDescription>
              Record fault details for this device.
            </DialogDescription>
          </DialogHeader>
          {selectedDevice && (
            <div className="space-y-4 py-4">
              <div className="rounded-md bg-muted p-3 text-sm">
                <p><strong>Device:</strong> {selectedDevice.deviceNumber || `Device #${selectedDevice.deviceNumber}`}</p>
                <p><strong>Device Number:</strong> #{selectedDevice.deviceNumber}</p>
                {selectedDevice.deviceTypeName && (
                  <p><strong>Type:</strong> {selectedDevice.deviceTypeName}</p>
                )}
              </div>
              <FaultDeviceComposer
                deviceNumber={selectedDevice.deviceNumber}
                showHeader={false}
                onSuccess={() => {
                  setIsFaultDialogOpen(false)
                  setSelectedDevice(null)
                  refetch()
                }}
                onError={(error) => {
                  log.error("Failed to mark device as faulty:", error)
                  toast({ title: 'Error', description: `Failed to mark device as faulty: ${error.message}`, type: 'error' })
                }}
                onCancel={() => {
                  setIsFaultDialogOpen(false)
                  setSelectedDevice(null)
                }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

